/*
 * Executes a parsed shell command by issuing the REST call described in the manifest, using the
 * same base URL as the generated API clients plus the browser session cookie and CSRF token.
 *
 * No new backend endpoint is involved: this calls the exact same /api/... endpoints the admin UI
 * already uses, so the existing per-endpoint permission checks apply unchanged.
 */
import globalAxios, {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {XSRF} from "components/src/auth/Xsrf";
import {ShellBody, ShellCommand} from "./ShellManifest";
import {postProcessorFor} from "./ShellPostProcess";

export interface ExecResult {
    ok: boolean;
    status?: number;
    /** parsed JSON object/array, or undefined for non-JSON */
    json?: unknown;
    /** raw text when the response is not JSON */
    text?: string;
    /** error message for transport/HTTP failures */
    error?: string;
}

function baseUrl(): string {
    // Match the generated API clients exactly: an empty basePath means same-origin RELATIVE
    // requests (`/api/...`). The dev server proxies these to the backend, and in production the
    // UI is served from the same origin. Falling back to an absolute URL (e.g. http://localhost:8081)
    // would be cross-origin and is blocked by the page CSP (default-src/connect-src 'self').
    return ApiConfig.config().basePath ?? "";
}

function asScalar(v: string | string[] | undefined): string | undefined {
    return Array.isArray(v) ? v.join(",") : v;
}

/** Build the JSON value for an object/const body node from the option values. */
function buildBodyValue(node: ShellBody, values: Record<string, string | string[]>): unknown {
    if (node.kind === "const") {
        return node.value;
    }
    if (node.kind === "value") {
        const v = values[node.fromOption];
        return v === undefined ? null : v;
    }
    if (node.kind === "object") {
        const obj: Record<string, unknown> = {};
        for (const f of node.fields) {
            obj[f.name] = buildBodyValue(f.value, values);
        }
        return obj;
    }
    return null;  // a `file` body is only ever top-level (handled in execute)
}

export async function execute(
    command: ShellCommand,
    values: Record<string, string | string[]>,
    fileProvider?: () => Promise<File | null>,
): Promise<ExecResult> {
    const http = command.http;
    if (!http) {
        return {ok: false, error: "command is not executable from the web shell"};
    }

    // substitute {name} path variables from their mapped option or constant
    let path = http.path;
    for (const pv of http.pathVars) {
        const value = pv.const ?? (pv.fromOption ? asScalar(values[pv.fromOption]) : undefined);
        if (value === undefined) {
            return {ok: false, error: `missing value for path variable {${pv.name}}`};
        }
        path = path.replace(`{${pv.name}}`, encodeURIComponent(value));
    }

    // build query params from their mapped option (when it has a value) or constant
    const params: Record<string, string> = {};
    for (const q of http.query) {
        const value = q.const ?? (q.fromOption ? asScalar(values[q.fromOption]) : undefined);
        if (value !== undefined) {
            params[q.param] = value;
        }
    }

    // multipart upload: post form-data. A `file` part is filled from a locally picked file; a
    // `stringAsFile` part sends a string option's value as in-memory file content (mirroring the
    // CLI's ByteArrayResource), so the picker only opens when a `file` part is present.
    if (http.multipart) {
        const needsFile = http.multipart.parts.some(p => p.kind === "file");
        const file = needsFile && fileProvider ? await fileProvider() : null;
        if (needsFile && !file) {
            return {ok: false, error: "no file selected"};
        }
        const form = new FormData();
        for (const part of http.multipart.parts) {
            if (part.kind === "file" && file) {
                form.append(part.name, file, file.name);
            } else if (part.kind === "stringAsFile") {
                form.append(part.name,
                    new Blob([asScalar(values[part.fromOption]) ?? ""], {type: "text/plain"}), part.name);
            } else {
                // a plain form field. A list-valued option is sent as a single application/json array
                // part (mirroring the Java client's MultiValueMap encoding, which serializes a List
                // value as JSON), so the backend's @RequestPart List<...> deserialization accepts it.
                // A non-list value is sent as a plain text field.
                const raw = values[part.fromOption];
                const opt = command.options.find(o => o.name === part.fromOption);
                if (Array.isArray(raw) || opt?.type === "list") {
                    form.append(part.name, new Blob(
                        [JSON.stringify(Array.isArray(raw) ? raw : raw === undefined ? [] : [raw])],
                        {type: "application/json"}));
                } else {
                    form.append(part.name, asScalar(raw) ?? "");
                }
            }
        }
        return send(http.method, baseUrl() + path, params, form, {"X-XSRF-TOKEN": XSRF.getCookie()});
    }

    // build the request body (if any), mirroring the Java client's converters:
    // a list -> JSON array (application/json), anything else -> raw text (text/plain)
    const headers: Record<string, string> = {"X-XSRF-TOKEN": XSRF.getCookie()};
    let requestData: string | Blob | undefined;
    if (http.body) {
        const b = http.body;
        if (b.kind === "file") {
            // read a locally picked file and send its content as the body
            const file = fileProvider ? await fileProvider() : null;
            if (!file) {
                return {ok: false, error: "no file selected"};
            }
            if (b.encoding === "text") {
                requestData = await file.text();
                headers["Content-Type"] = "text/plain";
            } else if (b.encoding === "lines") {
                requestData = JSON.stringify((await file.text()).split(/\r?\n/).filter(l => l.trim() !== ""));
                headers["Content-Type"] = "application/json";
            } else {
                requestData = file;  // raw bytes
                headers["Content-Type"] = "application/octet-stream";
            }
        } else if (b.kind === "value") {
            // a single option: a list -> JSON array (application/json), else raw text (text/plain)
            const raw = values[b.fromOption];
            const opt = command.options.find(o => o.name === b.fromOption);
            if (Array.isArray(raw) || opt?.type === "list") {
                requestData = JSON.stringify(Array.isArray(raw) ? raw : raw === undefined ? [] : [raw]);
                headers["Content-Type"] = "application/json";
            } else {
                requestData = raw ?? "";
                headers["Content-Type"] = "text/plain";
            }
        } else {
            // const or object body -> JSON
            requestData = JSON.stringify(buildBodyValue(b, values));
            headers["Content-Type"] = "application/json";
        }
    }

    // download: stream the response and save it as a local file. When the download is optional and
    // no filename was given, the command returns the content inline, so render it instead.
    if (http.download) {
        const wanted = asScalar(values[http.download.filenameOption]);
        if (!(http.download.optional && !wanted)) {
            const filename = (wanted ? wanted.split(/[\\/]/).pop() : "") || `${command.words.join("-")}.dat`;
            return download(http.method, baseUrl() + path, params, requestData, headers, filename);
        }
    }

    const result = await send(http.method, baseUrl() + path, params, requestData, headers);

    // apply a hand-written client-side transform (filter/pluck/sort) for commands the Spring Shell
    // implements with post-processing the generic call can't replicate
    const transform = postProcessorFor(command.words);
    if (transform && result.ok) {
        const out = transform(result.json !== undefined ? result.json : result.text, values);
        if (out !== undefined && out !== null && typeof out === "object") {
            return {ok: true, status: result.status, json: out};
        }
        return {ok: true, status: result.status, text: out === undefined || out === null ? "" : String(out)};
    }
    return result;
}

async function download(
    method: string,
    url: string,
    params: Record<string, string>,
    data: unknown,
    headers: Record<string, string>,
    filename: string,
): Promise<ExecResult> {
    try {
        const response = await globalAxios.request({
            method, url, params, data, withCredentials: true, headers,
            responseType: "blob",
            validateStatus: () => true,
        });
        const blob = response.data as Blob;
        if (response.status < 200 || response.status >= 300) {
            // error responses come back as a blob too; surface them as text/JSON
            const text = await blob.text();
            try {
                return {ok: false, status: response.status, json: JSON.parse(text)};
            } catch {
                return {ok: false, status: response.status, text};
            }
        }
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        return {ok: true, status: response.status, text: `Downloaded ${filename} (${blob.size} bytes)`};
    } catch (err) {
        const ax = err as AxiosError;
        return {ok: false, error: ax.message || "download failed"};
    }
}

async function send(
    method: string,
    url: string,
    params: Record<string, string>,
    data: unknown,
    headers: Record<string, string>,
): Promise<ExecResult> {
    try {
        const response = await globalAxios.request({
            method,
            url,
            params,
            data,
            withCredentials: true,
            headers,
            // never throw on non-2xx; we render the body ourselves
            validateStatus: () => true,
        });

        const ok = response.status >= 200 && response.status < 300;
        const body = response.data;
        if (body !== undefined && body !== null && body !== "" && typeof body === "object") {
            return {ok, status: response.status, json: body};
        }
        const text = body === undefined || body === null || body === "" ? "" : String(body);
        return {ok, status: response.status, text};
    } catch (err) {
        const ax = err as AxiosError;
        return {ok: false, error: ax.message || "request failed"};
    }
}
